import { artemisService } from './artemis.service';
import { ApiService } from './api.service';
import { environment } from '../config/environment';
import { StorageUtil } from '../utils/storage.util';
import { convertToProxyUrl, isDevelopment } from '../utils/proxy.util';
import { setGA4UserId } from '../utils/ga4.util';
import { logger } from '../utils/logger.util';
import type {
  AuthLoginHeaderEnrichmentRequest,
  AuthLoginHeaderEnrichmentResponse,
  HeaderEnrichmentResponse,
  AuthLoginResponse
} from '../models/auth.models';
import axios from 'axios';

export class AuthService {
  /**
   * Obtém os dados do Header Enrichment da Vivo
   */
  private async getHeaderEnrichment(): Promise<HeaderEnrichmentResponse> {
    try {
      const url = isDevelopment()
        ? convertToProxyUrl(environment.headerEnrichmentUrl)
        : environment.headerEnrichmentUrl;

      // Criar FormData para multipart/form-data (como no curl)
      const formData = new FormData();
      formData.append('conntype', 'cellular');
      formData.append('appid', 'VIVO-INTERNET-GRATIS');

      // Obter a origem atual da página para o header Origin
      const origin = window.location.origin;
      const referer = window.location.href;

      const response = await axios.post(
        url,
        formData,
        {
          headers: {
            // Não definir Content-Type manualmente - o axios vai definir automaticamente
            // com o boundary correto para multipart/form-data
            'Origin': origin,
            'Referer': referer
          }
        }
      );

      const data = response.data;

      if (!data.token || !data.msisdn) {
        throw new Error('Token ou MSISDN não encontrados na resposta do Header Enrichment');
      }

      return {
        token: data.token,
        msisdn: data.msisdn
      };
    } catch (error: unknown) {
      const errorMessage = error instanceof Error ? error.message : 'Erro desconhecido';
      throw new Error(`Erro ao obter Header Enrichment: ${errorMessage}`);
    }
  }

  /**
   * Autentica usando Header Enrichment
   */
  private async postLoginHeaderEnrichment(
    request: AuthLoginHeaderEnrichmentRequest,
    provider: string = 'MDIVO'
  ): Promise<AuthLoginHeaderEnrichmentResponse> {
    try {
      // Para web, usar a rota /auth/he
      // Usar axios diretamente para ter acesso aos headers da resposta
      const token = StorageUtil.getToken();
      const url = isDevelopment()
        ? convertToProxyUrl(`${environment.apiBaseUrl}auth/he`)
        : `${environment.apiBaseUrl}auth/he`;

      const response = await axios.post(
        url,
        {
          he: request.msisdn,
        },
        {
          headers: {
            'X-PROVIDER': provider,
            ...ApiService.getDefaultHeaders(token),
          }
        }
      );

      // O token JWT vem no header X-Authorization
      const accessToken = response.headers['x-authorization'] ||
                         response.headers['X-Authorization'] ||
                         response.data?.accessToken;

      if (!accessToken) {
        throw new Error('Token de acesso não encontrado na resposta');
      }

      return {
        msisdn: response.data.msisdn || '',
        accessToken: accessToken,
        id: response.data.id || response.data.walletId || ''
      };
    } catch (error: unknown) {
      let errorMessage = 'Erro na autenticação';
      if (typeof error === 'object' && error !== null && 'response' in error) {
        const axiosError = error as { response?: { data?: { message?: string } } };
        if (axiosError.response?.data?.message) {
          errorMessage = axiosError.response.data.message;
        }
      } else if (error instanceof Error && error.message) {
        errorMessage = error.message;
      }
      throw new Error(errorMessage);
    }
  }

  /**
   * Realiza login completo com Header Enrichment
   * Retorna null se falhar para permitir fallback para SMS
   */
  async loginWithHeaderEnrichment(provider: string = 'MDIVO'): Promise<AuthLoginHeaderEnrichmentResponse | null> {
    try {
      // 1. Obter token e msisdn do header enrichment
      const enrichmentData = await this.getHeaderEnrichment();

      // 2. Validar campos obrigatórios
      if (!enrichmentData.token || !enrichmentData.msisdn) {
        return null; // Fallback para SMS
      }

      // 3. Autenticar com os dados obtidos
      const authResponse = await this.postLoginHeaderEnrichment({
        token: enrichmentData.token,
        msisdn: enrichmentData.msisdn
      }, provider);

      // 4. Armazenar token e dados do usuário
      StorageUtil.setToken(authResponse.accessToken);
      StorageUtil.setUserData({
        msisdn: authResponse.msisdn,
        id: authResponse.id
      });

      // 5. Configurar user_id no GA4
      if (authResponse.id) {
        setGA4UserId(authResponse.id);
      }

      // 6. Configurar userId e headers na SDK do Artemis Ads
      try {
        logger.logWithContext('AuthService', 'Configurando Artemis SDK após login com Header Enrichment...');
        await artemisService.setUserId(authResponse.id);
        await artemisService.setCustomHeaders(authResponse.accessToken);
        logger.logWithContext('AuthService', 'Artemis SDK configurada após login com Header Enrichment');
      } catch (artemisError: unknown) {
        logger.warnWithContext('AuthService', 'Erro ao configurar Artemis SDK após login (continuando):', artemisError);
        // Não bloquear o login se a SDK falhar
      }

      return authResponse;
    } catch (error: unknown) {
      // Se falhar, retorna null para permitir fallback
      logger.warnWithContext('AuthService', 'Header Enrichment failed, falling back to SMS login', error);
      return null;
    }
  }

  /**
   * Solicita envio de código SMS
   */
  async requestSMSCode(msisdn: string): Promise<void> {
    const cleanMsisdn = msisdn.replace(/\D/g, '');
    if (cleanMsisdn.length !== 11) {
      throw new Error('Número de telefone inválido. Deve ter 11 dígitos (DDD + número)');
    }

    try {
      const url = isDevelopment()
        ? convertToProxyUrl(`${environment.apiBaseUrl}pnde`)
        : `${environment.apiBaseUrl}pnde`;

      const response = await axios.post(
        url,
        { msisdn: cleanMsisdn },
        {
          headers: {
            ...ApiService.getDefaultHeaders(),
            'X-USER-ID': cleanMsisdn,
            'X-API-TOKEN': '' // Pode ser null/empty para web
          }
        }
      );

      if (response.status !== 200) {
        throw new Error('Falha ao solicitar código SMS');
      }
    } catch (error: unknown) {
      let errorMessage = 'Erro ao solicitar código SMS. Verifique o número e tente novamente.';
      if (typeof error === 'object' && error !== null && 'response' in error) {
        const axiosError = error as { response?: { data?: { message?: string } } };
        if (axiosError.response?.data?.message) {
          errorMessage = axiosError.response.data.message;
        }
      } else if (error instanceof Error && error.message) {
        errorMessage = error.message;
      }
      throw new Error(errorMessage);
    }
  }

  /**
   * Valida código SMS e realiza login
   */
  async loginWithSMS(msisdn: string, code: string): Promise<AuthLoginResponse> {
    // Validar formato do telefone (11 dígitos)
    const cleanMsisdn = msisdn.replace(/\D/g, '');
    if (cleanMsisdn.length !== 11) {
      throw new Error('Número de telefone inválido. Deve ter 11 dígitos (DDD + número)');
    }

    // Validar código (6 dígitos)
    const cleanCode = code.replace(/\D/g, '');
    if (!/^\d{6}$/.test(cleanCode)) {
      throw new Error('Código inválido. Deve ter 6 dígitos');
    }

    try {
      const url = isDevelopment()
        ? convertToProxyUrl(`${environment.apiBaseUrl}vapi`)
        : `${environment.apiBaseUrl}vapi`;

      const response = await axios.post(
        url,
        { token: cleanCode },
        {
          headers: {
            ...ApiService.getDefaultHeaders(),
            'X-USER-ID': cleanMsisdn,
            'X-PINCODE': cleanCode,
            'X-API-TOKEN': '' // Pode ser null/empty para web
          }
        }
      );

      // O token JWT vem no header X-Authorization
      const accessToken = response.headers['x-authorization'] ||
                         response.headers['X-Authorization'];

      if (!accessToken) {
        throw new Error('Token de acesso não encontrado na resposta');
      }

      const authResponse: AuthLoginResponse = {
        id: response.data.id || '',
        accessToken: accessToken,
        msisdn: cleanMsisdn,
        warning: response.data.warning
      };

      // Armazenar token e dados do usuário
      StorageUtil.setToken(authResponse.accessToken);
      StorageUtil.setUserData({
        msisdn: authResponse.msisdn || cleanMsisdn,
        id: authResponse.id
      });

      // Configurar user_id no GA4
      if (authResponse.id) {
        setGA4UserId(authResponse.id);
      }

      // Configurar userId e headers na SDK do Artemis Ads
      try {
        logger.logWithContext('AuthService', 'Configurando Artemis SDK após login com SMS...');
        await artemisService.setUserId(authResponse.id);
        await artemisService.setCustomHeaders(authResponse.accessToken);
        logger.logWithContext('AuthService', 'Artemis SDK configurada após login com SMS');
      } catch (artemisError: unknown) {
        logger.warnWithContext('AuthService', 'Erro ao configurar Artemis SDK após login (continuando):', artemisError);
        // Não bloquear o login se a SDK falhar
      }

      return authResponse;
    } catch (error: unknown) {
      let errorMessage = 'Erro ao validar código. Tente novamente.';
      if (typeof error === 'object' && error !== null && 'response' in error) {
        const axiosError = error as { response?: { data?: { message?: string } } };
        if (axiosError.response?.data?.message) {
          // Usar diretamente a mensagem da API
          errorMessage = axiosError.response.data.message;
        }
      } else if (error instanceof Error && error.message) {
        errorMessage = error.message;
      }
      throw new Error(errorMessage);
    }
  }

  /**
   * Valida a sessão atual do usuário
   * Faz POST para /auth para verificar se o token ainda é válido
   */
  async validateSession(): Promise<boolean> {
    const token = StorageUtil.getToken();
    if (!token) {
      return false;
    }

    try {
      const url = isDevelopment()
        ? convertToProxyUrl(`${environment.apiBaseUrl}auth`)
        : `${environment.apiBaseUrl}auth`;

      const response = await axios.post(
        url,
        '', // POST vazio (body vazio)
        {
          headers: {
            ...ApiService.getDefaultHeaders(token),
            'X-IGNORE-SESSION-EXPIRED': 'true'
          }
        }
      );

      // Se chegou aqui, a sessão é válida
      // O token pode ter sido atualizado no header X-Authorization
      const newToken = response.headers['x-authorization'] ||
                      response.headers['X-Authorization'] ||
                      token;

      // Atualizar token se foi renovado
      if (newToken !== token) {
        StorageUtil.setToken(newToken);
      }

      // Obter dados do usuário para configurar Artemis
      const userData = StorageUtil.getUserData();
      if (userData) {
        // Configurar user_id no GA4
        if (userData.id) {
          setGA4UserId(userData.id);
        }

        // Configurar userId e headers na SDK do Artemis Ads
        try {
          logger.logWithContext('AuthService', 'Configurando Artemis SDK após validação de sessão...');
          await artemisService.setUserId(userData.id);
          await artemisService.setCustomHeaders(newToken);
          logger.logWithContext('AuthService', 'Artemis SDK configurada após validação de sessão');
        } catch (artemisError: any) {
          logger.warnWithContext('AuthService', 'Erro ao configurar Artemis SDK após validação (continuando):', artemisError);
          // Não bloquear se a SDK falhar
        }
      }

      return true;
    } catch (error: unknown) {
      // Se der erro 401 ou qualquer outro erro, a sessão expirou
      let statusOrMessage = 'Erro desconhecido';
      if (error instanceof Error) {
        statusOrMessage = error.message;
      } else if (typeof error === 'object' && error !== null && 'response' in error) {
        const axiosError = error as { response?: { status?: number } };
        statusOrMessage = String(axiosError.response?.status || 'Erro na requisição');
      }
      logger.warnWithContext('AuthService', 'Sessão inválida ou expirada:', statusOrMessage);
      return false;
    }
  }

  /**
   * Verifica se o usuário está autenticado
   */
  isAuthenticated(): boolean {
    return StorageUtil.getToken() !== null;
  }

  /**
   * Realiza logout
   */
  async logout(): Promise<void> {
    // Limpar user_id do GA4
    setGA4UserId(null);

    // Limpar SDK do Artemis
    try {
      await artemisService.logout();
    } catch (error: unknown) {
      logger.warnWithContext('AuthService', 'Erro ao limpar SDK do Artemis no logout:', error);
      // Não bloquear o logout se a SDK falhar
    }

    // Limpar storage
    StorageUtil.clear();
  }

  /**
   * Obtém o token atual
   */
  getToken(): string | null {
    return StorageUtil.getToken();
  }
}

export const authService = new AuthService();

